import React, { forwardRef } from 'react'
import NextLink, { LinkProps as NextLinkProps } from 'next/link'
import { PseudoBox, PseudoBoxProps } from '@chakra-ui/core'

const baseStyleProps = {
  transition: `all 0.15s ease-out`,
  cursor: 'pointer',
  textDecoration: 'none',
  outline: 'none',
  _focus: {
    boxShadow: 'outline',
  },
  _disabled: {
    opacity: 0.4,
    cursor: 'not-allowed',
    textDecoration: 'none',
  },
}

export interface BaseLinkProps extends PseudoBoxProps {
  isDisabled?: boolean
  isExternal?: boolean
  href?: string
}

export const BaseLink = forwardRef(
  ({ isDisabled, onClick, isExternal, ...restProps }: BaseLinkProps, ref) => {
    const externalProps = isExternal ? { target: '_blank', rel: 'noopener noreferrer' } : {}

    return (
      <PseudoBox
        ref={ref}
        as="a"
        tabIndex={isDisabled ? -1 : undefined}
        aria-disabled={isDisabled}
        onClick={isDisabled ? event => event.preventDefault() : onClick}
        fontWeight="medium"
        _hover={{ textDecoration: 'underline' }}
        {...externalProps}
        {...restProps}
      />
    )
  },
)

/////////////////////////////////////////////////////////////////////////////////////////////////

export interface LinkProps extends Omit<NextLinkProps, 'as'>, Omit<PseudoBoxProps, 'href'> {
  hrefAs?: NextLinkProps['as']
  isDisabled?: boolean
  isExternal?: boolean
}

export const Link = forwardRef(
  (
    {
      href,
      hrefAs,
      passHref = true,
      replace,
      scroll,
      shallow,
      prefetch,
      isExternal,
      ...restProps
    }: LinkProps,
    ref,
  ) => {
    if (isExternal) {
      return (
        <BaseLink
          ref={ref}
          href={href as string}
          isExternal={isExternal}
          {...baseStyleProps}
          {...restProps}
        />
      )
    }

    return (
      <NextLink
        href={href}
        as={hrefAs}
        passHref={passHref}
        replace={replace}
        scroll={scroll}
        shallow={shallow}
        prefetch={prefetch}
      >
        <BaseLink ref={ref} {...baseStyleProps} {...restProps} />
      </NextLink>
    )
  },
)

export default Link
